import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { rootFontPx } from './rootFont';

/** The narrowest the panel opens or is dragged to, and the room it leaves beside it. Both in rem, as the root font sets them. */
const MIN_WIDTH_REM = 30;
const DRAG_MARGIN_REM = 4;
/** Default panel width as a fraction of the main area (space left of the chat). */
const DEFAULT_WIDTH_FRACTION = 0.66;

export interface UseSlideOutPanelChromeArgs {
  isOpen: boolean;
  hasSource: boolean;
  onClose: () => void;
  /** Width of the Ask Moo chat container (px) */
  chatWidth: number;
  /** Right offset of the chat container (px) — needed for floating mode */
  chatRight?: number;
  /** Width (px) the panel opens at until the user drags it. Omitted, it is
   *  the responsive default: two thirds of the main area. */
  defaultWidth?: number;
  /** Whether the hook closes the panel on Esc. Pass false when the caller
   *  decides itself whether an Esc is the panel's (a dialog or menu above it
   *  owns its own Esc). Default true. */
  closeOnEscape?: boolean;
}

export interface UseSlideOutPanelChromeResult {
  visible: boolean;
  panelRef: RefObject<HTMLDivElement | null>;
  titleId: string;
  rightOffset: number;
  actualWidth: number;
  isExpanded: boolean;
  toggleExpanded: () => void;
  isResizing: boolean;
  handleResizeStart: (e: React.MouseEvent) => void;
}

/**
 * Shared chrome for the right-docked citation slide-out panels
 * (ThreatActorPanel, CvePanel): backdrop, slide, resize, expand, focus-trap
 * wiring, Esc-to-close, inert-toggle-when-closed, and focus-restore.
 * Extracted so panels that differ only in header/body content don't
 * duplicate this ~100 lines of state/effects (Sonar CPD flagged the
 * duplication when CvePanel was first cloned from ThreatActorPanel).
 *
 * Accessibility contract preserved from the original panels:
 * - Focus moves into the panel on open; restored to the trigger on close.
 * - Focus is trapped within the panel while open (caller wraps in
 *   `<FocusTrap active={visible}>` — trapping itself isn't this hook's job).
 * - Esc closes the panel and stops the event from also closing the Ask Moo
 *   chat shell, which listens for Esc separately.
 * - Resize handle accessible via mouse (keyboard resize not needed for panel chrome).
 */
export function useSlideOutPanelChrome({
  isOpen,
  hasSource,
  onClose,
  chatWidth,
  chatRight = 0,
  defaultWidth: initialWidth,
  closeOnEscape = true,
}: UseSlideOutPanelChromeArgs): UseSlideOutPanelChromeResult {
  const [isExpanded, setIsExpanded] = useState(false);
  // Manual drag-resize width (px). Null until the user drags, so the panel
  // tracks the responsive 66%-of-main-area default until then.
  const [userWidth, setUserWidth] = useState<number | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const minWidth = MIN_WIDTH_REM * rootFontPx();
  const cleanupRef = useRef<(() => void) | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();

  const visible = isOpen && hasSource;

  const rightOffset = chatWidth + chatRight;
  // Main area = viewport minus the chat container docked on the right.
  const mainArea = viewportWidth - rightOffset;
  const expandedWidth = chatWidth ? mainArea : viewportWidth;
  const defaultWidth =
    initialWidth ??
    Math.max(minWidth, Math.round(mainArea * DEFAULT_WIDTH_FRACTION));
  const panelWidth = userWidth ?? defaultWidth;
  const actualWidth = isExpanded ? expandedWidth : panelWidth;

  // Keep the responsive default in sync with viewport changes while open.
  useEffect(() => {
    if (!visible) return;
    const onResize = () => setViewportWidth(window.innerWidth);
    setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [visible]);

  // Esc-to-close — stops propagation so the Ask Moo shell's own (unguarded)
  // Esc listener doesn't also close the whole chat sidebar in the same
  // keystroke, and doesn't stomp a popover that already handled it.
  useEffect(() => {
    if (!visible || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, closeOnEscape, onClose]);

  // Move focus into panel on open; restore to trigger on close.
  useEffect(() => {
    if (visible) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
      // Small rAF so the panel is painted before we try to focus it.
      const id = requestAnimationFrame(() => {
        panelRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    } else {
      previouslyFocused.current?.focus?.();
      previouslyFocused.current = null;
    }
    return undefined;
  }, [visible]);

  // When closed but still mounted (kept for the slide-out transition), mark the
  // dialog subtree inert so its off-screen controls leave the tab order and
  // assistive tech stops treating it as an open modal. `inert` is a DOM property
  // not typed as a React prop in React 18, so toggle it imperatively.
  useEffect(() => {
    panelRef.current?.toggleAttribute('inert', !visible);
  }, [visible]);

  // On unmount: clean up resize listeners, and if we unmount while still open
  // restore focus to the element that opened the panel. previouslyFocused is
  // null after a normal close, so this is a no-op in that case.
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      previouslyFocused.current?.focus?.();
    };
  }, []);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = panelWidth;
      setIsResizing(true);

      const onMove = (me: MouseEvent) => {
        const delta = startX - me.clientX;
        const lowest = MIN_WIDTH_REM * rootFontPx();
        const max = Math.max(
          lowest,
          window.innerWidth - rightOffset - DRAG_MARGIN_REM * rootFontPx()
        );
        setUserWidth(Math.max(lowest, Math.min(max, startW + delta)));
      };
      const onUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        cleanupRef.current = null;
      };

      cleanupRef.current = onUp;
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [panelWidth, rightOffset]
  );

  return {
    visible,
    panelRef,
    titleId,
    rightOffset,
    actualWidth,
    isExpanded,
    toggleExpanded: () => setIsExpanded((v) => !v),
    isResizing,
    handleResizeStart,
  };
}
